import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../../../components/ui/sheet';
import { isActiveTransferState, transferCleanupOffer } from '../model/transferJobRules';
import { transferJobWithinScope } from '../../../core/engines/azureStorageTransferForget';
import { trackPanelToggle, useTransferActions } from '../hooks/useTransferActions';
import { useCollisionAutoDecide } from '../hooks/useCollisionAutoDecide';
import { requestForgetTransfer } from '../hooks/forgetTransfer';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { selectOrderedJobs, useTransfersStore } from '../store/transfersStore';
import { useTransfersPanelStore } from '../store/transfersPanelStore';
import { restoreTransfersPanelFocus } from '../services/transfersPanelFocus';
import { TransferCollisionDialog } from './TransferCollisionDialog';
import { TransferItemErrors } from './TransferItemErrors';
import { TransferJobRow } from './TransferJobRow';
import { TransferHistoryNotice } from './TransferHistoryNotice';
import { refreshTransfers } from '../hooks/useTransferEvents';
import { trackTransferEvent } from '../services/transfersApi';
import { openConfirmDialog } from '../../workspace/common/components/confirm/confirmDialogStore';
import { ACL_RESTART_TITLE, aclRestartConfirmRequest, aclRestartCopy } from './aclRestartConfirm';
import { TypedNameConfirmPanel } from '../../workspace/storage-explorer/hns/dialogs/TypedNameConfirmPanel';
import { propagateAgainFromFailure } from './aclPropagateAgain';
import { CLEAR_FINISHED_HINT, DISCARD_UNFINISHED_HINT, clearFinishedConfirmation, isFullyFinished } from '../model/clearFinishedCopy';
import { ReasonButton } from '../../../components/ui/ReasonButton';
import { X } from 'lucide-react';
import { STORAGE_READ_ONLY_REASON } from '../../../core/engines/azureStorageRefusal';

/** N17 (C1): the reason Resume / Restart show on a transfer whose connection was removed. */
export const TRANSFER_CONNECTION_REMOVED = 'The connection this transfer used was removed. Forget… or Discard unfinished… removes it from the list.';

export function TransfersPanel() {
  const open = useTransfersPanelStore((state) => state.open);
  const close = useTransfersPanelStore((state) => state.close);
  const transferState = useTransfersStore();
  const actions = useTransferActions();
  const jobs = selectOrderedJobs(transferState);
  const active = jobs.filter((job) => isActiveTransferState(job.state));
  const finished = jobs.filter((job) => !isActiveTransferState(job.state));
  // Only a live job asks (a terminal snapshot never carries a prompt; this guards an older main that emitted one).
  const pendingCollisionJob = active.find((job) => job.pendingCollision);
  const collisionJob = useCollisionAutoDecide(pendingCollisionJob, pendingCollisionJob ? transferState.collisionRules[pendingCollisionJob.id] : undefined, actions.decide);
  const connections = useConnectionStore((state) => state.connections);
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const cleanupOffer = (job: (typeof jobs)[number]) => {
    const found = connections.find((connection) => connection.id === job.connectionId);
    const connected = activeConnections.some((entry) => entry.id === job.connectionId && entry.status === 'connected');
    return transferCleanupOffer(job, { exists: !!found, readOnly: found?.readOnly === true, connected, withinScope: !!found && transferJobWithinScope(found.azureStorageScope ?? { kind: 'account' }, job) });
  };
  // N17 (C1): Resume / Restart cannot run without the job's connection, nor write through a read-only one — say why.
  const recoveryRefusal = (job: (typeof jobs)[number]): string | undefined => {
    const found = connections.find((connection) => connection.id === job.connectionId);
    if (!found) return TRANSFER_CONNECTION_REMOVED;
    return found.readOnly === true && job.kind !== 'download' && job.kind !== 'export' ? STORAGE_READ_ONLY_REASON : undefined;
  };
  // Z2: until the first read settles, or when it failed, "No … transfers" would be a claim the drawer cannot make.
  const { historyRead, hydrated } = transferState;
  const emptyLine = (none: string) => hydrated ? none : historyRead.state === 'failed' ? null : historyRead.state === 'loading' ? 'Loading transfers…' : none;
  const retryHistory = () => {
    trackTransferEvent('feature:storage_transfer_action', { action: 'history_retry' });
    void refreshTransfers(hydrated ? 'refresh' : 'initial');
  };
  const empty = (none: string) => { const line = emptyLine(none); return line ? <p className="text-xs text-muted-foreground">{line}</p> : null; };
  const closePanel = () => {
    if (!useTransfersPanelStore.getState().open) return;
    trackPanelToggle(false);
    close();
  };

  // S8 (D14): an `acl` job's Restart is confirmed first — typed name (inline, below the row) for replace / remove, plain for merge.
  const [typedRestart, setTypedRestart] = useState<string>();
  const restart = (job: (typeof jobs)[number]) => {
    if (recoveryRefusal(job)) return; // C1-05: every Restart path (row, item, typed panel) is refused the same way
    if (aclRestartCopy(job)?.typed) { setTypedRestart(job.id); return; }
    const confirm = aclRestartConfirmRequest(job, () => actions.restart(job.id));
    if (confirm) openConfirmDialog(confirm); else void actions.restart(job.id);
  };
  const typedPanel = (job: (typeof jobs)[number]) => {
    const copy = typedRestart === job.id ? aclRestartCopy(job) : undefined;
    if (!copy) return null;
    return <TypedNameConfirmPanel title={ACL_RESTART_TITLE} lines={copy.lines} name={copy.name} confirmLabel="Restart"
      onConfirm={() => { setTypedRestart(undefined); void actions.restart(job.id); }} onCancel={() => setTypedRestart(undefined)} />;
  };
  // U14: Clear finished never discards an unfinished transfer; Discard unfinished… names what goes first (F1).
  const fullyFinished = finished.filter(isFullyFinished);
  const unfinished = finished.filter((job) => !isFullyFinished(job));
  const discardUnfinished = () => {
    const copy = clearFinishedConfirmation(unfinished);
    if (!copy) { void actions.discardUnfinished(); return; }
    openConfirmDialog({ ...copy, description: <span data-testid="transfers-clear-confirmation">{copy.description}</span>, confirmLabel: 'Discard', variant: 'destructive', onConfirm: () => actions.discardUnfinished() });
  };
  // F5: one list item per job — the row, its typed Restart confirmation and its errors — so the divider comes after the
  // job's own "Show N errors", never between the row and them.
  const rows = (sectionJobs: typeof jobs) => <ul>{sectionJobs.map((job) => (
    <li key={job.id} className="border-b border-border py-2 last:border-b-0" data-testid={`transfer-job-${job.id}`}>
      <TransferJobRow
        job={job}
        pending={transferState.pending[job.id]}
        notice={transferState.rowNotices[job.id]}
        lastErrorMessage={transferState.lastErrors[job.id]?.message}
        throughputBps={transferState.throughputBps[job.id]}
        canReveal
        onCancel={(id) => void actions.cancel(id)}
        onResume={(id) => void actions.resume(id)}
        onRestart={() => restart(job)}
        onOpenFolder={actions.openFolder}
        onForget={cleanupOffer(job) === 'forget' ? (id) => void requestForgetTransfer(id) : undefined}
        connectToCleanUp={cleanupOffer(job) === 'connect'}
        recoveryRefusal={recoveryRefusal(job)}
      />
      {typedPanel(job)}
      {/* C1-05: the error details carry the row's recovery refusal; the callbacks are gated as well. */}
      <TransferItemErrors job={job} recoveryRefusal={recoveryRefusal(job)} onRetry={(id, items) => { if (!recoveryRefusal(job)) void actions.retryItems(id, items); }} onRestart={() => restart(job)}
        onPropagateAgain={(failedJob, failure) => { if (recoveryRefusal(failedJob)) return; closePanel(); propagateAgainFromFailure(failedJob, failure); }} />
    </li>
  ))}</ul>;

  return (
    <>
      <Sheet open={open} onOpenChange={(next) => { if (!next) closePanel(); }}>
        {/* U14: the list fills the panel's width under a header that stays put while the list scrolls. */}
        {/* R7 / N18 (C1): no Radix trigger exists, so focus returns to the opener explicitly (chip, Home, dialog card). */}
        <SheetContent className="flex flex-col p-0" data-testid="transfers-popover" onEscapeKeyDown={closePanel} onCloseAutoFocus={(event) => { event.preventDefault(); restoreTransfersPanelFocus(); }}>
          <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-surface-raised px-4 py-3">
            <div>
              <SheetTitle className="text-base font-semibold">Transfers</SheetTitle>
              {/* A21: the history survives a restart — it is not "for this app session". */}
              <SheetDescription className="text-xs text-muted-foreground">Uploads, downloads and storage changes. Finished transfers stay listed, also after a restart, until you clear them.</SheetDescription>
            </div>
            <Button variant="ghost" size="icon" className="size-7 shrink-0" aria-label="Close transfers" title="Close" onClick={closePanel} data-testid="transfers-close"><X className="h-4 w-4" /></Button>
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
            <TransferHistoryNotice read={historyRead} hasRows={jobs.length > 0} onRetry={retryHistory} />
            <section><h3 className="text-sm font-semibold">Active</h3><div className="mt-1">{active.length ? rows(active) : empty('No active transfers.')}</div></section>
            <section>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">Finished</h3>
                <div className="flex items-center gap-1">
                  <ReasonButton variant="outline" size="xs" reason={fullyFinished.length === 0 ? (unfinished.length > 0 ? 'Every finished transfer can still be resumed, restarted or cleaned up — discard them instead' : 'No finished transfers') : undefined} title={CLEAR_FINISHED_HINT} onClick={() => void actions.clearFinished()} data-testid="transfers-clear-finished">Clear finished</ReasonButton>
                  {unfinished.length > 0 && <Button variant="outline" size="xs" className="border-destructive text-destructive" title={DISCARD_UNFINISHED_HINT} onClick={discardUnfinished} data-testid="transfers-discard-unfinished">Discard unfinished…</Button>}
                </div>
              </div>
              <div className="mt-1">{finished.length ? rows(finished) : empty('No finished transfers.')}</div>
            </section>
          </div>
        </SheetContent>
      </Sheet>
      <TransferCollisionDialog key={collisionJob?.id ?? 'no-collision'} job={collisionJob} onDecide={actions.decide} onCancelJob={actions.cancelFromCollision} />
    </>
  );
}
